import { useEffect, useState } from "react";
import {
	getEnvironmentUsbDevices,
	MAX_ENVIRONMENT_USB_DEVICES,
	updateEnvironmentUsbDevices,
	type EnvironmentUsbDevices,
	type UsbDevice,
} from "@/lib/api";
import { isAbortError } from "@/lib/abort-error";
import { usbDeviceIdentity, usbDeviceName } from "@/lib/usb-devices";
import { EnvironmentSettingsForm } from "./EnvironmentSettingsForm";

interface EnvironmentUsbDevicesPanelProps {
	environmentId: string;
}

const CAPTION_STYLE = {
	fontSize: "var(--text-caption)",
	fontFamily: "var(--font-sans)",
} as const;

const TRUNCATE_STYLE = {
	minWidth: 0,
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
} as const;

/**
 * デバイスのエージェントに渡す USB 機器の選択。ランナーが報告した機器から選び、保存で選択を全て置き換える
 * (次の起動から反映)。使用中・未接続の機器も選べる (同じ機器の同時の利用は起動のときに断られる)。
 */
export function EnvironmentUsbDevicesPanel({ environmentId }: EnvironmentUsbDevicesPanelProps) {
	const [data, setData] = useState<EnvironmentUsbDevices | null>(null);
	const [selected, setSelected] = useState<string[]>([]);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const controller = new AbortController();
		setLoadError(null);
		getEnvironmentUsbDevices(environmentId, controller.signal)
			.then((result) => {
				setData(result);
				setSelected(result.selected);
			})
			.catch((err: unknown) => {
				if (isAbortError(err)) return;
				console.error("USB 機器の一覧を取得できませんでした", err);
				setLoadError(err instanceof Error ? err.message : "USB 機器の取得に失敗しました。");
			});
		return () => controller.abort();
	}, [environmentId]);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!data) return;
		setSubmitError(null);
		setSaved(false);
		setLoading(true);
		try {
			await updateEnvironmentUsbDevices(environmentId, selected);
			setSaved(true);
		} catch (err) {
			console.error("USB 機器の設定を保存できませんでした", err);
			setSubmitError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setLoading(false);
		}
	}

	const toggle = (key: string, checked: boolean) => {
		setSaved(false);
		setSelected((prev) => (checked ? [...prev, key] : prev.filter((k) => k !== key)));
	};

	const limitReached = selected.length >= MAX_ENVIRONMENT_USB_DEVICES;

	return (
		<EnvironmentSettingsForm
			testId="environment-usb-devices-panel"
			onSubmit={handleSubmit}
			loading={loading}
			saved={saved}
			submitError={submitError}
			submitDisabled={data === null}
		>
			<span style={{ ...CAPTION_STYLE, color: "var(--fg-2)" }}>
				デバイスに差した USB 機器 (ロボットなどのシリアル通信の機器やカメラ)
				をこのエージェントに渡します。
				{MAX_ENVIRONMENT_USB_DEVICES}
				台まで選べます。起動したあとに機器を差し直したときは、エージェントを起動し直してください。
			</span>
			{loadError && (
				<span
					data-testid="environment-usb-devices-error"
					style={{ ...CAPTION_STYLE, color: "var(--danger)" }}
				>
					{loadError}
				</span>
			)}
			{data && !data.runnerOnline && (
				<span
					data-testid="environment-usb-devices-offline"
					style={{ ...CAPTION_STYLE, color: "var(--warning)" }}
				>
					デバイスがオフラインです。最後に報告された一覧です。
				</span>
			)}
			{data === null && !loadError && (
				<span
					data-testid="environment-usb-devices-loading"
					style={{ ...CAPTION_STYLE, color: "var(--fg-3)" }}
				>
					読み込み中…
				</span>
			)}
			{data && data.devices.length === 0 && (
				<span
					data-testid="environment-usb-devices-empty"
					style={{ ...CAPTION_STYLE, color: "var(--fg-3)" }}
				>
					デバイスに差してある USB 機器はありません。機器を差すと、しばらくしてここに表示されます。
				</span>
			)}
			{data && data.devices.length > 0 && limitReached && (
				<span
					data-testid="environment-usb-devices-limit"
					style={{ ...CAPTION_STYLE, color: "var(--fg-2)" }}
				>
					{MAX_ENVIRONMENT_USB_DEVICES}
					台を選んでいます。ほかの機器を選ぶには、どれかを外してください。
				</span>
			)}
			{data && data.devices.length > 0 && (
				<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
					{data.devices.map((device) => {
						const checked = selected.includes(device.key);
						const overLimit = !checked && limitReached;
						return (
							<UsbDeviceRow
								key={device.key}
								device={device}
								checked={checked}
								overLimit={overLimit}
								disabled={loading || overLimit}
								onChange={(next) => toggle(device.key, next)}
							/>
						);
					})}
				</div>
			)}
		</EnvironmentSettingsForm>
	);
}

function UsbDeviceRow({
	device,
	checked,
	overLimit,
	disabled,
	onChange,
}: {
	device: UsbDevice;
	checked: boolean;
	overLimit: boolean;
	disabled: boolean;
	onChange: (checked: boolean) => void;
}) {
	const name = usbDeviceName(device);
	const details = [usbDeviceIdentity(device), ...device.nodes].join(" · ");
	return (
		<label
			data-testid={`environment-usb-device-${device.key}`}
			style={{
				display: "flex",
				alignItems: "flex-start",
				gap: 8,
				fontFamily: "var(--font-sans)",
				cursor: disabled ? "not-allowed" : "pointer",
				opacity: overLimit ? 0.5 : 1,
			}}
		>
			<input
				type="checkbox"
				checked={checked}
				disabled={disabled}
				onChange={(e) => onChange(e.target.checked)}
				data-testid={`environment-usb-device-checkbox-${device.key}`}
				style={{ marginTop: 3 }}
			/>
			<span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
				<span style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
					<span
						title={name}
						data-tooltip-if-truncated
						style={{ ...TRUNCATE_STYLE, fontSize: "var(--text-ui)", color: "var(--fg-1)" }}
					>
						{name}
					</span>
					<UsbDeviceState device={device} />
				</span>
				<span
					title={details}
					data-tooltip-if-truncated
					style={{
						...TRUNCATE_STYLE,
						fontSize: "var(--text-caption)",
						color: "var(--fg-3)",
						fontFamily: "var(--font-mono)",
					}}
				>
					{details}
				</span>
			</span>
		</label>
	);
}

function connectionState(device: UsbDevice): string | null {
	if (device.state === "removed") return "抜かれています";
	if (device.state === "replugged") {
		return "差し直されました。使えないときはエージェントを起動し直してください";
	}
	return device.connected ? null : "つながっていません";
}

function UsbDeviceState({ device }: { device: UsbDevice }) {
	const states = [
		connectionState(device),
		device.usedBy
			? device.usedBy.environmentId === null
				? `${device.usedBy.name}が使っています`
				: `「${device.usedBy.name}」が使っています`
			: null,
	].filter((s): s is string => s !== null);
	if (states.length === 0) return null;
	const text = states.join("・");
	return (
		<span
			data-tooltip={text}
			data-tooltip-if-truncated
			data-testid={`environment-usb-device-state-${device.key}`}
			style={{ ...CAPTION_STYLE, ...TRUNCATE_STYLE, maxWidth: "50%", color: "var(--warning)" }}
		>
			{text}
		</span>
	);
}
